import { Fragment, useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * Nav - ClariCommand editorial sidebar (paper-2 bg, mono-numbered items, teal active-border).
 *
 * Pure presentational. Groups come from the `groups` prop; the active item is keyed off `activeId`;
 * clicking an item calls `onNavigate(item.id)`. Collapse state is local (number-only mini-strip when
 * collapsed). No router, no stores (the AppShell wires onNavigate to react-router navigate()).
 */

export interface NavItem {
  id: string;
  num: string;
  name: string;
  meta?: string;
  /**
   * Optional count badge (since row 866 the Inbound queue's open count, lib/inbound-open-count.ts).
   * Rendered only when > 0, as navBadgeText writes it: exact under 1,000, then "1.2k", "3k", "12k".
   */
  badge?: number;
  /** The badge's tooltip in plain words (e.g. "746 open items in the Inbound queue"). */
  badgeTitle?: string;
  /** Row 400: nested under the item above it (indented in the list; its own tile in the strip). */
  sub?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export interface NavProps {
  groups: NavGroup[];
  activeId: string;
  onNavigate: (id: string) => void;
}

interface StripItem extends NavItem {
  groupIndex: number;
  itemIndex: number;
}

/**
 * The badge's text (row 866). The exact number up to 999; above that, thousands cut DOWN to one
 * decimal under ten thousand ("1.2k", "3k"), then whole thousands ("12k"), then millions ("1.2M").
 * Never rounded up, so the badge never claims more than there is; the tooltip carries the exact number.
 */
export function navBadgeText(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${Math.floor(n / 100) / 10}k`;
  if (n < 1_000_000) return `${Math.floor(n / 1000)}k`;
  return `${Math.floor(n / 100_000) / 10}M`;
}

/** The badge's tooltip: the item's own words, else the exact number. */
function badgeTitleOf(item: NavItem): string {
  return item.badgeTitle ?? (item.badge ?? 0).toLocaleString('en-US');
}

export function Nav({ groups, activeId, onNavigate }: NavProps) {
  const [collapsed, setCollapsed] = useState(false);

  const stripItems: StripItem[] = groups.flatMap((g, gi) =>
    g.items.map((it, ii) => ({ ...it, groupIndex: gi, itemIndex: ii })),
  );

  return (
    <nav className={cn(collapsed && 'collapsed')} aria-label="primary navigation">
      <button
        type="button"
        className="nav-toggle"
        onClick={() => setCollapsed((c) => !c)}
        title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      >
        {collapsed ? '▶' : '◀'}
      </button>

      {/* Expanded: full grouped list */}
      <div className="nav-body side-pad">
        {groups.map((g) => (
          <div key={g.label} className="side-group">
            <div className="side-label">{g.label}</div>
            {g.items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate(item.id)}
                className={cn('side-item', item.sub && 'sub', item.id === activeId && 'active')}
                style={{ width: '100%', textAlign: 'left', textDecoration: 'none' }}
              >
                <span className="num">{item.num}</span>
                <span className="name">{item.name}</span>
                <span className="meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {item.badge ? (
                    <span className="nav-badge" title={badgeTitleOf(item)}>
                      {navBadgeText(item.badge)}
                    </span>
                  ) : null}
                  {item.meta && <span>{item.meta}</span>}
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Collapsed: number-only mini-strip with group separators */}
      <div className="nav-strip">
        {stripItems.map((it, i) => {
          const isActive = it.id === activeId;
          const prev = i > 0 ? stripItems[i - 1] : undefined;
          const showSep = prev !== undefined && it.groupIndex !== prev.groupIndex;
          return (
            <Fragment key={it.id}>
              {showSep && <div className="nav-strip-sep" />}
              <button
                type="button"
                onClick={() => onNavigate(it.id)}
                className={cn('nav-strip-item', isActive && 'active')}
                title={it.badge ? `${it.num} · ${it.name} · ${badgeTitleOf(it)}` : `${it.num} · ${it.name}`}
                style={{ textDecoration: 'none', position: 'relative' }}
              >
                <span className="num">{it.num}</span>
                {it.badge ? <span className="nav-strip-badge" aria-hidden="true" /> : null}
              </button>
            </Fragment>
          );
        })}
      </div>
    </nav>
  );
}
